import { useQuery } from "@tanstack/react-query";
import lodash from "lodash";

import categoryService from "app/common/services/categoryService";
import { DefaultFetchService } from "app/services/fetchService";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type { IDemographicsApiAllData, IDemographicsApiParams } from "../types";

import { usePageFilters } from "./usePageFilters";

const DEMOGRAPHICS_QUERY_KEY = "demographics-data";
const ALL_DEMOGRAPHICS_API_ENDPOINT = "/widgetApi/DemographicSegmentsIndustry/Data";
const TIME_GRANULARITY = GranularityNames.MONTHLY;

const prepareApiParams = (params: IDemographicsApiParams) => {
    const { country, from, to, category, webSource, includeSubDomains = true } = params;

    if (!category) {
        return;
    }

    const categoryObject = categoryService.categoryQueryParamToCategoryObject(category);

    return lodash.pickBy(
        {
            country,
            from,
            to,
            includeSubDomains,
            keys: categoryObject.forApi,
            category: categoryObject.forDisplayApi,
            categoryHash: categoryObject.isCustomCategory ? categoryObject.categoryHash : "",
            timeGranularity: TIME_GRANULARITY,
            webSource,
        },
        (p) => !lodash.isUndefined(p),
    );
};

export const fetchDemographicsData = async (params: IDemographicsApiParams): Promise<IDemographicsApiAllData> => {
    const fetchService = DefaultFetchService.getInstance();
    const demographicsApiParams = prepareApiParams(params);

    const response = await fetchService.get<IDemographicsApiAllData>(
        ALL_DEMOGRAPHICS_API_ENDPOINT,
        demographicsApiParams,
    );
    return response;
};

export const useDemographicsData = <TSelected = IDemographicsApiAllData>(options?: {
    select?: (data: IDemographicsApiAllData) => TSelected;
}) => {
    const pageFilters = usePageFilters();

    return useQuery({
        queryKey: [DEMOGRAPHICS_QUERY_KEY, pageFilters],
        queryFn: () => fetchDemographicsData(pageFilters),
        select: options?.select,
    });
};
